import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Loader2, Minus, Plus, Send, UtensilsCrossed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { money } from "@/lib/format";
import { toast } from "sonner";
import "@/styles/qr-menu-themes.css";

const publicApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
});

function BrowseMenu({ data, category, setCategory }) {
  const cats = data.categories || [];
  const filtered = useMemo(() => {
    if (!category || category === "all") return cats;
    return cats.filter((c) => c.name === category);
  }, [cats, category]);

  const showImages = data.show_item_images !== false;
  const currency = data.branch?.currency || "";

  return (
    <>
      <header className="qr-menu-hero">
        {data.logo_url ? (
          <img src={data.logo_url} alt="" className="qr-menu-logo" />
        ) : null}
        <h1 className="qr-menu-brand">{data.branch?.name}</h1>
        {data.tagline ? <p className="qr-menu-tagline">{data.tagline}</p> : null}
        {data.table ? (
          <p className="qr-menu-tagline" style={{ opacity: 0.75 }}>
            Table {data.table.code}
            {data.table.area ? ` · ${data.table.area}` : ""}
          </p>
        ) : null}
        {data.ramadan_mode && data.ramadan_message ? (
          <p
            className="qr-menu-tagline"
            style={{
              marginTop: "0.85rem",
              background: "rgba(255,255,255,0.12)",
              display: "inline-block",
              padding: "0.35rem 0.65rem",
              borderRadius: "0.5rem",
            }}
          >
            {data.ramadan_message}
          </p>
        ) : null}
      </header>

      {cats.length > 1 ? (
        <div className="qr-menu-cats">
          <button
            type="button"
            className="qr-menu-cat-chip"
            data-active={category === "all"}
            onClick={() => setCategory("all")}
          >
            All
          </button>
          {cats.map((c) => (
            <button
              key={c.name}
              type="button"
              className="qr-menu-cat-chip"
              data-active={category === c.name}
              onClick={() => setCategory(c.name)}
            >
              {c.name}
            </button>
          ))}
        </div>
      ) : null}

      <main>
        {filtered.map((cat) => (
          <section key={cat.name} className="qr-menu-section">
            <h2>{cat.name}</h2>
            {cat.items.map((item) => (
              <article
                key={item.id}
                className={`qr-menu-item${showImages && item.image_url ? " qr-menu-item-with-image" : ""}`}
              >
                {showImages && item.image_url ? <img src={item.image_url} alt="" /> : null}
                <div>
                  <h3 className="qr-menu-item-name">{item.name}</h3>
                  {item.description ? <p className="qr-menu-item-desc">{item.description}</p> : null}
                </div>
                <div className="qr-menu-item-price">{money(item.price, currency)}</div>
              </article>
            ))}
          </section>
        ))}
        <p className="qr-menu-footer-note">Prices shown for reference · Ask your server to order</p>
      </main>
    </>
  );
}

export default function QrMenu() {
  const { branchCode, tableCode } = useParams();
  const [cart, setCart] = useState({});
  const [guestName, setGuestName] = useState("");
  const [sent, setSent] = useState(false);
  const [category, setCategory] = useState("all");

  const endpoint = tableCode
    ? `/public/menu/${branchCode}/table/${tableCode}`
    : `/public/menu/${branchCode}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ["qr-menu", branchCode, tableCode],
    queryFn: async () => {
      const { data: res } = await publicApi.get(endpoint);
      return res;
    },
    enabled: Boolean(branchCode),
  });

  const canSelfOrder = Boolean(tableCode && data?.self_order_enabled);
  const theme = data?.theme || "warm_spice";
  const currency = data?.branch?.currency || "";

  const cartLines = useMemo(() => {
    if (!data?.categories) return [];
    const items = data.categories.flatMap((c) => c.items);
    return Object.entries(cart)
      .map(([id, qty]) => {
        const item = items.find((i) => String(i.id) === String(id));
        return item ? { ...item, qty } : null;
      })
      .filter(Boolean);
  }, [cart, data]);

  const cartTotal = cartLines.reduce((s, l) => s + l.price * l.qty, 0);

  const orderMutation = useMutation({
    mutationFn: async () => {
      const lines = cartLines.map((l) => ({ menu_item_id: l.id, qty: l.qty }));
      const { data: res } = await publicApi.post(
        `/public/menu/${branchCode}/table/${tableCode}/order`,
        { guest_name: guestName || undefined, lines },
      );
      return res;
    },
    onSuccess: () => {
      setSent(true);
      setCart({});
      toast.success("Order sent to kitchen!");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Could not send order");
    },
  });

  function adjustQty(id, delta) {
    setCart((prev) => {
      const next = { ...prev };
      const qty = (next[id] || 0) + delta;
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="qr-menu-shell min-h-screen grid place-items-center" data-theme="warm_spice">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: "var(--qr-accent)" }} />
      </div>
    );
  }

  if (error) {
    const apiErrors = error.response?.data?.errors;
    const detail =
      (apiErrors && (apiErrors.branch?.[0] || apiErrors.table?.[0] || Object.values(apiErrors).flat()?.[0])) ||
      error.response?.data?.message ||
      "Menu unavailable";
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6 text-center max-w-md mx-auto">
        <div>
          <p className="text-slate-700 font-medium">{detail}</p>
          <p className="text-slate-500 text-sm mt-2">
            Enable the public QR menu in Administration → Restaurant Setup, then save.
          </p>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="qr-menu-shell min-h-screen grid place-items-center p-6 text-center" data-theme={theme}>
        <div>
          <UtensilsCrossed className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--qr-accent)" }} />
          <h1 className="qr-menu-brand" style={{ fontSize: "1.5rem", color: "var(--qr-ink)" }}>
            Order received
          </h1>
          <p className="qr-menu-item-desc mt-2">
            Table {data.table?.code} — kitchen has been notified.
          </p>
        </div>
      </div>
    );
  }

  // Branch browse URL (and table with self-order off): themed view-only menu
  if (!canSelfOrder) {
    return (
      <div className="qr-menu-shell" data-theme={theme}>
        <BrowseMenu data={data} category={category} setCategory={setCategory} />
      </div>
    );
  }

  // Table self-order: themed shell + qty controls
  const cats = data.categories || [];
  const filtered = category === "all" ? cats : cats.filter((c) => c.name === category);
  const showImages = data.show_item_images !== false;

  return (
    <div className="qr-menu-shell pb-28" data-theme={theme}>
      <header className="qr-menu-hero">
        {data.logo_url ? <img src={data.logo_url} alt="" className="qr-menu-logo" /> : null}
        <h1 className="qr-menu-brand">{data.branch?.name}</h1>
        {data.tagline ? <p className="qr-menu-tagline">{data.tagline}</p> : null}
        <p className="qr-menu-tagline" style={{ opacity: 0.75 }}>
          Table {data.table?.code}
          {data.table?.area ? ` · ${data.table.area}` : ""}
        </p>
      </header>

      {cats.length > 1 ? (
        <div className="qr-menu-cats">
          <button type="button" className="qr-menu-cat-chip" data-active={category === "all"} onClick={() => setCategory("all")}>
            All
          </button>
          {cats.map((c) => (
            <button
              key={c.name}
              type="button"
              className="qr-menu-cat-chip"
              data-active={category === c.name}
              onClick={() => setCategory(c.name)}
            >
              {c.name}
            </button>
          ))}
        </div>
      ) : null}

      <main>
        {filtered.map((cat) => (
          <section key={cat.name} className="qr-menu-section">
            <h2>{cat.name}</h2>
            {cat.items.map((item) => (
              <article
                key={item.id}
                className={`qr-menu-item${showImages && item.image_url ? " qr-menu-item-with-image" : ""}`}
              >
                {showImages && item.image_url ? <img src={item.image_url} alt="" /> : null}
                <div>
                  <h3 className="qr-menu-item-name">{item.name}</h3>
                  {item.description ? <p className="qr-menu-item-desc">{item.description}</p> : null}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="qr-menu-item-price">{money(item.price, currency)}</div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button type="button" size="icon" variant="outline" className="h-8 w-8" onClick={() => adjustQty(item.id, -1)}>
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-6 text-center text-sm font-mono">{cart[item.id] || 0}</span>
                    <Button type="button" size="icon" className="h-8 w-8" onClick={() => adjustQty(item.id, 1)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </section>
        ))}
      </main>

      {cartLines.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 border-t p-4 shadow-lg space-y-2 z-20" style={{ background: "var(--qr-surface)", borderColor: "var(--qr-border)" }}>
          <Input
            placeholder="Your name (optional)"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            className="h-9"
          />
          <Button
            className="w-full h-10"
            style={{ background: "var(--qr-accent)" }}
            disabled={orderMutation.isPending}
            onClick={() => orderMutation.mutate()}
          >
            {orderMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send order · {money(cartTotal, currency)}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
